import { useEffect, useState } from 'react';
import { Modal } from '@carbon/react';
import { TextInput } from '@mantine/core';
import { CategoryDto } from 'api/dtos/category';
import { defineMessages, useIntl } from 'react-intl';
import sharedMessages from 'Components/utils/sharedMessages';
import styles from './CategoryEditModal.module.scss';
import { useUpdateCategoryMutation } from 'api/categoryEndpoints';
import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';

interface CategoryEditModalProps {
  open: boolean;
  category: CategoryDto | null;
  onClose: () => void;
}

const CategoryEditModal = ({
    open,
    category,
    onClose,
}: CategoryEditModalProps) => {
    const intl = useIntl();
    const [currentColor, setCurrentColor] = useState('#000000');
    const [currentName, setCurrentName] = useState('');
    const [updateCategory, { isLoading }] = useUpdateCategoryMutation();
    const currentEntityId = useGetCurrentEntityId();

    useEffect(() => {
        if (category && category.color) {
            setCurrentColor(category.color);
        }
        if (category && category.name) {
            setCurrentName(category.name);
        }
    }, [category]);

    const onSave = async () => {
        if (!category) {
            return;
        }
        await updateCategory({
            id: category.id,
            data: {
                name: currentName.trim(),
                color: currentColor.trim(),
            },
            entityId: currentEntityId || '',
        });
        onClose();
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCurrentName(e.target.value);
    };

    return (
        <Modal
            preventCloseOnClickOutside
            open={open}
            modalHeading={intl.formatMessage(messages.changeInfo, { name: category?.name })}
            primaryButtonText={intl.formatMessage(sharedMessages.save)}
            secondaryButtonText={intl.formatMessage(sharedMessages.cancel)}
            onRequestClose={onClose}
            loadingStatus={isLoading ? 'active' : undefined}
            onRequestSubmit={onSave}
            primaryButtonDisabled={isLoading || currentName.trim().length < 3}
        >
            <div className={styles.container}>
                <TextInput
                    id="category-edit-name-input"
                    label={intl.formatMessage(messages.title)}
                    onChange={onChange}
                    value={currentName}
                    placeholder={intl.formatMessage(messages.titlePlaceholder)}
                    size="md"
                    type="text"
                />
                <div className={styles.colorField}>
                    <input
                        type="color"
                        value={currentColor}
                        onChange={(e) => { setCurrentColor(e.target.value.toUpperCase()); }}
                        className={styles.colorInput}
                    />
                    <span>{currentColor}</span>
                </div>
            </div>
        </Modal>
    );
};

const messages = defineMessages({
    changeInfo: {
        id: 'CategoryEditModal.changeInfo',
        defaultMessage: 'Zmień dane dla kategorii "{name}"',
    },
    title: {
        id: 'src.Screens.Dashboard.EventsConfiguration.Inputs.CategoryTags.CategoryEditModal.title',
        defaultMessage: 'Nazwa kategorii',
    },
    titlePlaceholder: {
        id: 'src.Screens.Dashboard.EventsConfiguration.Inputs.CategoryTags.CategoryEditModal.titlePlaceholder',
        defaultMessage: 'Wprowadź nazwę kategorii',
    },
});

export default CategoryEditModal;
